<script setup>
import { useMeeting } from '@/composables/useMeeting'
import { useApi } from '@/utils/api'
import { useRouter } from 'vue-router'

definePage({
  meta: {
    layout: 'default',
    action: 'create',
    subject: 'Meeting',
  },
})

const router = useRouter()
const { saving, scheduleMeeting } = useMeeting()

const form = ref({ title: '', unit_id: null, scheduled_at: '', location: '', agendas: [], participant_ids: [] })
const errors = ref({})

const addAgenda = () => form.value.agendas.push({ topic: '', description: '' })
const removeAgenda = index => form.value.agendas.splice(index, 1)

// Peserta autocomplete — pola sama persis pages/quality/capa/index.vue
// (Module 17) & components/shared/TaskList.vue (butuh user.view).
const participantOptions = ref([])
const participantSearchLoading = ref(false)

async function searchParticipants(query) {
  participantSearchLoading.value = true
  try {
    const response = await useApi('/api/v1/users', { query: { search: query, per_page: 20 } }).get()
    const data = response?.data ?? response
    const rows = Array.isArray(data) ? data : (data?.data ?? [])

    participantOptions.value = rows.map(u => ({ title: u.name, value: u.id }))
  } catch (e) {
    participantOptions.value = []
  } finally {
    participantSearchLoading.value = false
  }
}

const save = async () => {
  errors.value = {}
  try {
    const meeting = await scheduleMeeting(form.value)

    router.push({ name: 'meeting-id', params: { id: meeting.id } })
  } catch (e) {
    if (e?.status === 422) errors.value = e?.data?.errors ?? {}
    else console.error('Schedule meeting error:', e)
  }
}
</script>

<template>
  <div>
    <h4 class="text-h4 font-weight-bold mb-1">
      Jadwalkan Rapat
    </h4>
    <p class="text-body-2 text-medium-emphasis mb-4">
      Undangan terkirim otomatis ke peserta terdaftar via Notification Engine
    </p>

    <VCard max-width="800">
      <VCardText class="pa-6">
        <VRow>
          <VCol
            cols="12"
            md="6"
          >
            <VTextField
              v-model="form.title"
              label="Judul Rapat *"
              :error-messages="errors.title"
            />
          </VCol>
          <VCol
            cols="12"
            md="6"
          >
            <MasterDataPicker
              v-model="form.unit_id"
              entity="units"
              label="Unit (opsional)"
            />
          </VCol>
          <VCol
            cols="12"
            md="6"
          >
            <VTextField
              v-model="form.scheduled_at"
              type="datetime-local"
              label="Tanggal/Waktu *"
              :error-messages="errors.scheduled_at"
            />
          </VCol>
          <VCol
            cols="12"
            md="6"
          >
            <VTextField
              v-model="form.location"
              label="Lokasi"
            />
          </VCol>
          <VCol cols="12">
            <VAutocomplete
              v-model="form.participant_ids"
              :items="participantOptions"
              :loading="participantSearchLoading"
              label="Peserta"
              multiple
              chips
              closable-chips
              @update:search="searchParticipants"
            />
          </VCol>
        </VRow>

        <VDivider class="my-4" />

        <div class="d-flex align-center justify-space-between mb-3">
          <p class="text-body-1 font-weight-medium mb-0">
            Agenda
          </p>
          <VBtn
            size="small"
            variant="tonal"
            prepend-icon="ri-add-line"
            @click="addAgenda"
          >
            Tambah Agenda
          </VBtn>
        </div>

        <VRow
          v-for="(agenda, index) in form.agendas"
          :key="index"
          class="mb-2"
        >
          <VCol cols="10">
            <VTextField
              v-model="agenda.topic"
              :label="`Topik ${index + 1}`"
              density="compact"
            />
          </VCol>
          <VCol
            cols="2"
            class="d-flex align-center"
          >
            <VBtn
              icon="ri-delete-bin-line"
              variant="text"
              color="error"
              size="small"
              @click="removeAgenda(index)"
            />
          </VCol>
        </VRow>
        <p
          v-if="!form.agendas.length"
          class="text-body-2 text-medium-emphasis"
        >
          Belum ada agenda ditambahkan.
        </p>
      </VCardText>
      <VCardActions class="pa-6 pt-0">
        <VSpacer />
        <VBtn
          variant="tonal"
          color="secondary"
          :disabled="saving"
          @click="router.push({ name: 'meeting' })"
        >
          Batal
        </VBtn>
        <VBtn
          color="primary"
          :loading="saving"
          @click="save"
        >
          Simpan &amp; Undang
        </VBtn>
      </VCardActions>
    </VCard>
  </div>
</template>
